import React from 'react';
import { deletePrepaidCredit, } from '@Api';
import { IAbilities, IAuth, IOrganizationStore, } from '@Store';
import { CrudEnum, IPreviewPrepaidCredit, PermissionEnum, EnumTranslateTitle, TPrepaidCreditSortableListCol, } from 'atlas-shared';
import { tableColumnDatetimeSorter, timeSince, } from '@Utils';
import { TFunction, } from 'i18next';
import { NavigateFunction, } from 'react-router';
import { ColumnsType, } from 'antd/lib/table/interface';
import { listAuditButton, listCloneButton, listDeleteButton, listEditButton, GetColumnSearchProps, GetColumnSearchEqualProps, } from '../components';
import { IWithListProps, } from '@Hocs/with.list';

export const PrepaidCreditListColumns = (t: TFunction, navigate: NavigateFunction, showDeleted: boolean, organizations: IOrganizationStore, abilities: IAbilities, auth: IAuth, buttons: Array<(row: IPreviewPrepaidCredit) => React.ReactElement> = [], filters: IWithListProps<IPreviewPrepaidCredit, TPrepaidCreditSortableListCol>['filters'], sorter: IWithListProps<IPreviewPrepaidCredit, TPrepaidCreditSortableListCol>['sorter']): ColumnsType<IPreviewPrepaidCredit> => {
  return [
    {
      title: t('ID'),
      dataIndex: 'id',
      key: 'id',
      sorter: (a: IPreviewPrepaidCredit, b: IPreviewPrepaidCredit) => a.id - b.id,
      sortDirections: ['descend', 'ascend', ],
      sortOrder: sorter.find(s => s.column === 'id')?.order || null,
      filteredValue: filters.id || null,
      ...GetColumnSearchEqualProps(t, 'id', t('ID')),
      className: 'table-cell-id',
    },
    auth.user.__admin_organization_ids.length > 1 && {
      title: t('ORGANIZATION'),
      dataIndex: 'organization_id',
      key: 'organization_id',
      render: (organization_id: IPreviewPrepaidCredit['organization_id'], prepaid_credit: IPreviewPrepaidCredit) => organizations.dict?.[organization_id]?.title || t('ORGANIZATION_NOT_FOUND', { replace: { organization_id, }, }),
      sorter: (a: IPreviewPrepaidCredit, b: IPreviewPrepaidCredit) => (organizations.dict?.[a.organization_id]?.title || '').localeCompare(organizations.dict?.[b.organization_id]?.title || ''),
      sortDirections: ['descend', 'ascend', ],
      sortOrder: sorter.find(s => s.column === 'organization_id')?.order || null,
      filteredValue: filters.organization_id || null,
      ...GetColumnSearchProps(t, 'organization_id', t('ORGANIZATION'), organizations.organizations),
      className: 'table-cell-organization_id',
    },
    {
      title: t('CURRENCY'),
      dataIndex: 'currency',
      key: 'currency',
      render: (currency: IPreviewPrepaidCredit['currency'], prepaid_credit: IPreviewPrepaidCredit) => EnumTranslateTitle('INVOICE_CURRENCY', currency, t),
      sorter: (a: IPreviewPrepaidCredit, b: IPreviewPrepaidCredit) => EnumTranslateTitle('INVOICE_CURRENCY', a.currency, t).localeCompare(EnumTranslateTitle('INVOICE_CURRENCY', b.currency, t)),
      sortDirections: ['descend', 'ascend', ],
      sortOrder: sorter.find(s => s.column === 'currency')?.order || null,
      className: 'table-cell-currency',
    },
    {
      title: t('AMOUNT'),
      dataIndex: 'amount',
      key: 'amount',
      className: 'table-cell-amount',
    },
    {
      title: t('CREDIT_USED'),
      dataIndex: 'credit_used',
      key: 'credit_used',
      className: 'table-cell-credit_used',
    },
    {
      title: t('CREATED_AT'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (created_at: IPreviewPrepaidCredit['created_at'], prepaid_credit: IPreviewPrepaidCredit) => <div className='date-column'>{created_at ? timeSince(t, new Date(created_at), true) : ''}</div>,
      sorter: (a: IPreviewPrepaidCredit, b: IPreviewPrepaidCredit) => tableColumnDatetimeSorter(a.created_at, b.created_at),
      sortDirections: ['descend', 'ascend', ],
      sortOrder: sorter.find(s => s.column === 'created_at')?.order || null,
      className: 'table-cell-created_at',
    },
    
    {
      title: '',
      dataIndex: 'operation',
      key: 'operation',
      render: (_: any, row: IPreviewPrepaidCredit) => (
        <div className={'table-operation-wrp'}>
          {buttons.map(button => button(row))}
          {abilities.can(row.organization_id, CrudEnum.Insert, PermissionEnum.PrepaidCredit, row) && listCloneButton(t, navigate, 'prepaid_credit', row)}
          {abilities.can(row.organization_id, CrudEnum.Read, PermissionEnum.Audit, row) && listAuditButton(t, navigate, 'prepaid_credit', row)}
          {abilities.can(row.organization_id, CrudEnum.Update, PermissionEnum.PrepaidCredit, row) && listEditButton(t, navigate, 'prepaid_credit', row)}
          {abilities.can(row.organization_id, CrudEnum.Delete, PermissionEnum.PrepaidCredit, row) && listDeleteButton(t, row, deletePrepaidCredit)}
        </div>
      ),
    },
  ].filter(Boolean) as ColumnsType<IPreviewPrepaidCredit>;

};
